/**
 * OfflineBanner — persistent alert when network is unavailable.
 *
 * Reads from UIStore.isOnline (wired to NetInfo in root layout).
 * Renders below safe area with warning styling and retry action.
 */

import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Animated, { FadeOutUp } from 'react-native-reanimated'
import NetInfo from '@react-native-community/netinfo'
import { WifiOff } from '@/components/ui/icons'
import { Pressable } from './Pressable'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, Z_INDEX, SPACING, RADIUS, DURATION } from '../../lib/theme/tokens'
import { FONTS } from '../../lib/brand'
import { useUIStore } from '../../lib/stores/ui.store'
import { WEB_SHADOWS } from '../../lib/theme/web-shadow'
import { useEntrance } from '@/lib/hooks/use-entrance'

export function OfflineBanner() {
  const enter = useEntrance()
  const isOnline = useUIStore((s) => s.isOnline)
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()

  if (isOnline) return null

  return (
    <View
      // Prop, not a style key — see the note in components/ui/Toast.tsx.
      // react-native-web drops `box-none` from an inline style, so this
      // container used to be a full-width dead band across the top nav for the
      // whole time the device was offline.
      pointerEvents="box-none"
      style={{
        position: 'absolute',
        top: insets.top + SPACING.xs,
        left: 0,
        right: 0,
        zIndex: Z_INDEX.fixed,
      }}
      testID="offline-banner"
    >
      <Animated.View
        collapsable={false}
        entering={enter()}
        exiting={FadeOutUp.duration(DURATION.fast)}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginHorizontal: SPACING.md,
          paddingHorizontal: SPACING.sm,
          paddingVertical: SPACING.xs,
          borderRadius: RADIUS.md,
          backgroundColor: colors.surface,
          borderWidth: 1,
          borderColor: colors.warning,
          ...WEB_SHADOWS.md,
        }}
        accessibilityRole="alert"
        accessibilityLiveRegion="assertive"
      >
        <WifiOff size={18} color={colors.warning} style={{ marginRight: SPACING.xs }} />

        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 13,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
            numberOfLines={1}
          >
            {t('errors.offline_title')}
          </Text>
          <Text
            style={{
              fontSize: 12,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
            numberOfLines={1}
          >
            {t('errors.offline_desc')}
          </Text>
        </View>

        <Pressable
          onPress={() => NetInfo.refresh()}
          style={{
            minHeight: 44,
            justifyContent: 'center',
            marginLeft: SPACING.xs,
            paddingHorizontal: SPACING.sm,
          }}
          accessibilityRole="button"
          accessibilityLabel={t('errors.offline_action')}
          testID="offline-banner.retry-button"
        >
          <Text
            style={{
              fontSize: 13,
              fontFamily: FONTS.bodyBold,
              color: colors.warning,
            }}
            numberOfLines={1}
            adjustsFontSizeToFit
          >
            {t('errors.offline_action')}
          </Text>
        </Pressable>
      </Animated.View>
    </View>
  )
}
